import styled, { css } from 'styled-components';
import Table from 'components/Table/Table';
import {
  StyledHeaderCell,
  FilterRow as HeaderFilterRow,
  LabelSortRow,
  Label,
  SortWrapper,
} from 'components/Table/TableHeaderCell.styled';
import {
  FilterRow as ColumnFilterRow,
  FilterInput,
  FilterSelect,
  DateRangeButton,
} from 'components/Table/ColumnFilter.styled';
import { Wrapper as IconButton } from 'components/ButtonIcon/ButtonIcon.styled';

/**
 * The invoice of an application as the Member Association reads it: a green
 * tick once IPMA marked it paid, a red cross while it is not. Only the green
 * profile switches it, so this side never offers a control.
 */
export const InvoiceIcon = styled.div`
  display: inline-flex;
  justify-content: center;

  svg {
    width: 14px;
    height: 14px;
    fill: ${({ paid }) => (paid ? '#227211' : '#c62828')};
    pointer-events: none;
  }
`;

/**
 * The applicant of one row: three parts on one line, cut off with an ellipsis
 * rather than pushing the columns next to it out of shape. The whole line is
 * on the title attribute, so nothing is lost by the shortening.
 */
export const ApplicantCell = styled.span`
  display: block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/**
 * What a status is waiting for, under the status itself: "Submitted" alone does
 * not say whether it sits with the chair or with IPMA.
 */
export const StatusNote = styled.span`
  display: block;
  ${({ theme }) => theme && `color: ${theme.colors.gray[500]};`}
  font-size: 12px;
`;

/**
 * Row actions line up on one height: the icon buttons are 40px squares, while a
 * labelled button would otherwise stand taller on its own padding.
 */
const ACTION_HEIGHT = '40px';

/** Every filter control lines up on this height, whatever type it is. */
const FILTER_HEIGHT = '42px';

/** White calendar, drawn inline so it needs no asset and stays white on the orange square. */
const CALENDAR_ICON =
  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><rect x='3' y='4.5' width='18' height='17' rx='2'/><line x1='16' y1='2.5' x2='16' y2='6.5'/><line x1='8' y1='2.5' x2='8' y2='6.5'/><line x1='3' y1='10' x2='21' y2='10'/></svg>\")";

/**
 * The meeting tables: the plain, small table, with a filter and a sorter in the
 * header of each column.
 *
 * The header cell those two live in is shared with the wide CIS lists, where it
 * reserves 90px for a filter stacked on top of a label. Here it reads label +
 * sort first and carries the filter underneath, in one compact block. The rules
 * are scoped to this component, so the CIS screens keep their layout.
 */
const MeetingsTable = styled(Table)`
  table-layout: fixed;

  /* The 40px action buttons set the baseline of a row, which drags the text of
     the other cells down with it - and a status that carries a second line
     stretches the row further. Every cell centres on the row instead.
     Below the table breakpoint the cells are blocks, where this does nothing. */
  td {
    vertical-align: middle;
  }

  /* Whatever a row offers - Apply, Edit, View, the colleagues of the
     association - it is the same height as the button next to it. */
  td:last-child button {
    height: ${ACTION_HEIGHT};
    min-height: ${ACTION_HEIGHT};
    padding-top: 0;
    padding-bottom: 0;
  }

  /*
   * A table whose columns carry no filter: the header keeps the empty filter
   * row of the shared cell, which leaves it far taller than its one line of
   * text needs. Here that row goes and the label sits in the middle.
   */
  ${({ compactHeader }) => compactHeader && css`
    ${HeaderFilterRow} {
      display: none;
    }

    ${LabelSortRow} {
      margin-bottom: 0;
      min-height: 0;
    }

    ${StyledHeaderCell} {
      justify-content: center;
      min-height: 0;
    }
  `}

  /* The actions column only holds a button or two, so it is sized to that and
     the remaining width is split evenly over the real columns. Its label sits
     in the middle of the cell, there being no sort arrows to share it with. */
  th:last-child {
    width: ${({ actionsWidth }) => actionsWidth || '12rem'};
  }

  th:last-child ${Label} {
    flex: 1 1 100%;
    justify-content: center;
    text-align: center;
  }

  th:last-child ${SortWrapper} {
    display: none;
  }

  ${StyledHeaderCell} {
    height: auto;
    min-width: 0;
  }

  /* Label and sort on the first line, filter on the second. */
  ${LabelSortRow} {
    order: 1;
    min-height: 0;
    margin-bottom: 10px;
  }

  ${HeaderFilterRow} {
    order: 2;
    min-height: 0;
    width: 100%;
  }

  ${Label} {
    flex: 0 0 66.6667%;
    justify-content: flex-start;
    text-align: left;
  }

  ${SortWrapper} {
    flex: 0 0 33.3333%;
    justify-content: flex-end;
  }

  ${ColumnFilterRow} {
    width: 100%;
    max-width: none;
  }

  ${FilterInput} {
    height: ${FILTER_HEIGHT};
    box-sizing: border-box;
  }

  /* react-select sizes its control through emotion classes, which carry the
     name of the part they style. */
  ${FilterSelect} [class*='-control'],
  ${FilterSelect} [class*='-valueContainer'] {
    height: ${FILTER_HEIGHT} !important;
    min-height: ${FILTER_HEIGHT} !important;
  }

  ${FilterSelect} [class*='-valueContainer'] {
    padding: 0 0 0 8px;
  }

  ${FilterSelect} [class*='-indicatorContainer'] {
    padding: 4px;
  }

  /* Clear and apply buttons of a filter, 40px square by default. */
  ${ColumnFilterRow} ${IconButton} {
    width: ${FILTER_HEIGHT};
    height: ${FILTER_HEIGHT};
    min-height: ${FILTER_HEIGHT};

    svg {
      width: 14px;
      height: 14px;
    }
  }

  /* The date range filter is a button that reads empty until a range is
     picked, so it carries a calendar badge that says what it opens. */
  ${DateRangeButton} {
    position: relative;
    height: ${FILTER_HEIGHT};
    padding-right: 38px;
  }

  ${DateRangeButton}::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    width: 42px;
    background-color: ${({ theme }) => (theme && theme.colors && theme.colors.secondary) || '#f59c00'};
    background-image: ${CALENDAR_ICON};
    background-repeat: no-repeat;
    background-position: center;
    background-size: 14px 14px;
  }
`;

MeetingsTable.Head = Table.Head;
MeetingsTable.Body = Table.Body;
MeetingsTable.Row = Table.Row;
MeetingsTable.Heading = Table.Heading;
MeetingsTable.Cell = Table.Cell;
MeetingsTable.Label = Table.Label;

export default MeetingsTable;
